import React, { useState, useEffect } from 'react';
import { Mail, Send, CheckCircle2 } from 'lucide-react';
import PasswordInput from '../../common/PasswordInput';
import api from '../../../services/api';
import Breadcrumbs from '../../common/Breadcrumbs';
import './SettingsDetail.css';

const EmailSettings = () => {
  const [emailSettings, setEmailSettings] = useState({
    smtp_config: {
      host: '',
      port: 587,
      secure: false,
      auth: {
        user: '',
        pass: '',
      },
    },
    sender_info: {
      fromName: 'Operations Dashboard',
      fromEmail: '',
    },
  });

  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');
  const [testingConnection, setTestingConnection] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    loadEmailSettings();
  }, []);

  const loadEmailSettings = async () => {
    try {
      setEmailLoading(true);
      setEmailError('');
      const response = await api.email.getSettings();

      // Ensure proper structure with defaults
      const loadedSettings = {
        smtp_config: {
          host: response.data?.smtp_config?.host || '',
          port: response.data?.smtp_config?.port || 587,
          secure: response.data?.smtp_config?.secure || false,
          auth: {
            user: response.data?.smtp_config?.auth?.user || '',
            pass: response.data?.smtp_config?.auth?.pass || '',
          },
        },
        sender_info: {
          fromName: response.data?.sender_info?.fromName || 'Operations Dashboard',
          fromEmail: response.data?.sender_info?.fromEmail || '',
        },
      };

      setEmailSettings(loadedSettings);
    } catch (error) {
      console.error('Error loading email settings:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
      setEmailError(`Failed to load email settings: ${errorMsg}`);
    } finally {
      setEmailLoading(false);
    }
  };

  const handleEmailSettingChange = (section, field, value) => {
    setEmailSettings((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
    setEmailError('');
    setEmailSuccess('');
  };

  const handleAuthChange = (field, value) => {
    setEmailSettings((prev) => ({
      ...prev,
      smtp_config: {
        ...prev.smtp_config,
        auth: {
          ...prev.smtp_config.auth,
          [field]: value,
        },
      },
    }));
    setEmailError('');
    setEmailSuccess('');
  };

  const handleSaveEmailSettings = async (event) => {
    event.preventDefault();

    if (!emailSettings.smtp_config.host || !emailSettings.sender_info.fromEmail) {
      setEmailError('SMTP host and sender email are required');
      return;
    }

    try {
      setEmailLoading(true);
      setEmailError('');
      setEmailSuccess('');

      await api.email.saveSettings(emailSettings.smtp_config, emailSettings.sender_info);
      setEmailSuccess('Email settings saved successfully!');
    } catch (error) {
      console.error('Error saving email settings:', error);
      setEmailError(
        'Failed to save email settings: ' + (error.response?.data?.message || error.message)
      );
    } finally {
      setEmailLoading(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTestingConnection(true);
      setEmailError('');
      setEmailSuccess('');

      const response = await api.email.testConnection();
      if (response.data.success) {
        setEmailSuccess('Email connection test successful!');
      } else {
        setEmailError('Connection test failed: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      setEmailError('Connection test failed: ' + (error.response?.data?.message || error.message));
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail.trim()) {
      setEmailError('Please enter a test email address');
      return;
    }

    try {
      setSendingTestEmail(true);
      setEmailError('');
      setEmailSuccess('');

      await api.email.sendTestEmail(testEmail);
      setEmailSuccess(`Test email sent successfully to ${testEmail}!`);
      setTestEmail('');
    } catch (error) {
      console.error('Error sending test email:', error);
      setEmailError(
        'Failed to send test email: ' + (error.response?.data?.message || error.message)
      );
    } finally {
      setSendingTestEmail(false);
    }
  };

  const breadcrumbItems = [
    { label: 'Organization' },
    { label: 'Communications' },
    { label: 'Email Configuration' },
  ];

  return (
    <div className="settings-detail">
      <Breadcrumbs items={breadcrumbItems} />

      <div className="settings-detail-container">
        {/* Header */}
        <div className="settings-detail-header">
          <div className="settings-detail-header-icon">
            <Mail size={26} />
          </div>
          <div>
            <h1 className="settings-detail-title">Email Configuration</h1>
            <p className="settings-detail-description">
              Configure SMTP settings for email delivery and test your connection
            </p>
          </div>
        </div>

        {/* Alerts */}
        {emailError && (
          <div className="settings-alert settings-alert-error" role="alert">
            {emailError}
          </div>
        )}

        {emailSuccess && (
          <div className="settings-alert settings-alert-success" role="alert">
            {emailSuccess}
          </div>
        )}

        {/* Content */}
        {emailLoading ? (
          <div className="settings-loading">
            <div className="settings-spinner"></div>
            <p>Loading email settings...</p>
          </div>
        ) : (
          <div className="settings-detail-content">
            <form onSubmit={handleSaveEmailSettings} className="settings-form-sections">
              {/* SMTP Server Settings */}
              <div className="settings-card-detail">
                <div className="settings-card-header-detail">
                  <Mail size={20} />
                  <h2>SMTP Server Settings</h2>
                </div>

                <div className="settings-form-grid">
                  <div className="settings-form-group">
                    <label htmlFor="smtpHost" className="settings-form-label">
                      SMTP Host <span className="settings-form-required">*</span>
                    </label>
                    <input
                      type="text"
                      id="smtpHost"
                      value={emailSettings.smtp_config.host}
                      onChange={(e) =>
                        handleEmailSettingChange('smtp_config', 'host', e.target.value)
                      }
                      className="settings-form-input"
                      placeholder="smtp.zeptomail.com"
                      required
                    />
                  </div>

                  <div className="settings-form-group">
                    <label htmlFor="smtpPort" className="settings-form-label">
                      SMTP Port
                    </label>
                    <input
                      type="number"
                      id="smtpPort"
                      value={emailSettings.smtp_config.port}
                      onChange={(e) =>
                        handleEmailSettingChange('smtp_config', 'port', parseInt(e.target.value))
                      }
                      className="settings-form-input"
                      placeholder="587"
                    />
                  </div>
                </div>

                <div className="settings-form-group">
                  <label className="settings-form-checkbox">
                    <input
                      type="checkbox"
                      checked={emailSettings.smtp_config.secure}
                      onChange={(e) =>
                        handleEmailSettingChange('smtp_config', 'secure', e.target.checked)
                      }
                    />
                    <span>Use SSL/TLS (check for port 465, uncheck for port 587)</span>
                  </label>
                </div>
              </div>

              {/* Authentication */}
              <div className="settings-card-detail">
                <div className="settings-card-header-detail">
                  <CheckCircle2 size={20} />
                  <h2>Authentication</h2>
                </div>

                <div className="settings-form-grid">
                  <div className="settings-form-group">
                    <label htmlFor="smtpUser" className="settings-form-label">
                      Username/Email
                    </label>
                    <input
                      type="text"
                      id="smtpUser"
                      value={emailSettings.smtp_config.auth.user}
                      onChange={(e) => handleAuthChange('user', e.target.value)}
                      className="settings-form-input"
                      placeholder="your-email@example.com"
                    />
                  </div>

                  <div className="settings-form-group">
                    <label htmlFor="smtpPass" className="settings-form-label">
                      Password/App Password
                    </label>
                    <PasswordInput
                      id="smtpPass"
                      value={emailSettings.smtp_config.auth.pass}
                      onChange={(e) => handleAuthChange('pass', e.target.value)}
                      className="settings-form-input"
                      placeholder="••••••••"
                    />
                  </div>
                </div>
              </div>

              {/* Sender Information */}
              <div className="settings-card-detail">
                <div className="settings-card-header-detail">
                  <Send size={20} />
                  <h2>Sender Information</h2>
                </div>

                <div className="settings-form-grid">
                  <div className="settings-form-group">
                    <label htmlFor="fromName" className="settings-form-label">
                      From Name
                    </label>
                    <input
                      type="text"
                      id="fromName"
                      value={emailSettings.sender_info.fromName}
                      onChange={(e) =>
                        handleEmailSettingChange('sender_info', 'fromName', e.target.value)
                      }
                      className="settings-form-input"
                      placeholder="Operations Dashboard"
                    />
                  </div>

                  <div className="settings-form-group">
                    <label htmlFor="fromEmail" className="settings-form-label">
                      From Email <span className="settings-form-required">*</span>
                    </label>
                    <input
                      type="email"
                      id="fromEmail"
                      value={emailSettings.sender_info.fromEmail}
                      onChange={(e) =>
                        handleEmailSettingChange('sender_info', 'fromEmail', e.target.value)
                      }
                      className="settings-form-input"
                      placeholder="noreply@yourcompany.com"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="settings-form-actions">
                <button
                  type="submit"
                  disabled={emailLoading}
                  className="settings-btn settings-btn-primary"
                >
                  {emailLoading ? (
                    <>
                      <span className="settings-btn-spinner"></span>
                      Saving...
                    </>
                  ) : (
                    'Save Email Settings'
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleTestConnection}
                  disabled={testingConnection || !emailSettings.smtp_config.host}
                  className="settings-btn settings-btn-secondary"
                >
                  {testingConnection ? (
                    <>
                      <span className="settings-btn-spinner"></span>
                      Testing...
                    </>
                  ) : (
                    'Test Connection'
                  )}
                </button>
              </div>
            </form>

            {/* Test Email Section */}
            <div className="settings-card-detail">
              <div className="settings-card-header-detail">
                <Send size={20} />
                <h2>Send Test Email</h2>
              </div>

              <p className="settings-form-hint" style={{ marginBottom: '1rem' }}>
                Send a test email to verify your configuration is working correctly.
              </p>

              <div className="settings-form-inline">
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  className="settings-form-input"
                  placeholder="test@example.com"
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  onClick={handleSendTestEmail}
                  disabled={sendingTestEmail || !testEmail.trim()}
                  className="settings-btn settings-btn-success"
                >
                  {sendingTestEmail ? (
                    <>
                      <span className="settings-btn-spinner"></span>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Send Test Email
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailSettings;
